'use client'

import { FullScreenLoading } from '@/components/FullScreenLoading'
import ChangePasswordDialog from '@/components/dialog/ChangePasswordDialog'
import { useWhoAmI } from '@/hooks/useApi'
import { useRealtimeInvalidation } from '@/hooks/useRealtimeInvalidation'
import { useAuthStore } from '@/store/useAuthStore'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/auth/set-password',
  '/mfa',
  '/demo-components',
  '/demo',
  '/demo-login',
]

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const authToken = useAuthStore((state) => state.authToken)
  const passwordExpired = useAuthStore((state) => state.passwordExpired)
  const sessionGeneration = useAuthStore((state) => state.sessionGeneration)
  const [dismissedWarning, setDismissedWarning] = useState<string | null>(null)
  const [passwordAge, setPasswordAge] = useState<{
    key: string
    days: number | undefined
    dayToWarn: number | undefined
  } | null>(null)
  const isPublic =
    pathname === '/debug/chat' || PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
  const { isLoading, data } = useWhoAmI(!isPublic && !!authToken)
  useRealtimeInvalidation()

  useEffect(() => {
    if (data && authToken && !isPublic) {
      useAuthStore.getState().setUser(data)
      const key = `${sessionGeneration}:${data.id}`
      setPasswordAge((current) =>
        current?.key === key
          ? current
          : {
              key,
              days: data.password_age_settings?.num_day_til_expire ?? undefined,
              dayToWarn: data.password_age_settings?.day_to_warn ?? undefined,
            },
      )
    }
  }, [authToken, data, isPublic, sessionGeneration])

  useEffect(() => {
    if (!isPublic && !useAuthStore.getState().authToken) {
      router.replace('/login')
    }
  }, [authToken, isPublic, router])

  if (isPublic) return <>{children}</>

  if (isLoading) {
    return <FullScreenLoading />
  }

  if (!authToken || !data) {
    return null
  }

  const daysUntilExpiration = passwordAge?.days
  const dayToWarn = passwordAge?.dayToWarn
  const mustChangePassword = passwordExpired
  const warningKey = `${sessionGeneration}:${data.id}`
  const showWarning =
    daysUntilExpiration !== undefined &&
    dayToWarn !== undefined &&
    daysUntilExpiration > 0 &&
    daysUntilExpiration <= dayToWarn &&
    dismissedWarning !== warningKey

  if (mustChangePassword || showWarning) {
    return (
      <>
        {!mustChangePassword && children}
        <ChangePasswordDialog
          open
          daysUntilExpiration={mustChangePassword ? Math.min(daysUntilExpiration ?? 0, 0) : daysUntilExpiration}
          onOpenChange={(open) => {
            if (!open) setDismissedWarning(warningKey)
          }}
        />
      </>
    )
  }

  return <>{children}</>
}
